import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, GraduationCap, Heart, Briefcase, ArrowRight } from "lucide-react";

function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for reaching out! We'll get back to you within 24 hours.");
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#1167B1] to-[#0d5390] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-6">Get In Touch</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us & Enroll</h1>
          <p className="text-xl text-white/90 max-w-4xl mx-auto">
            Ready to empower your child with life-changing programs? Reach out to us today to learn more about enrollment, volunteering, or partnerships.
          </p>
        </div>
      </section>

      {/* PSLF / Join Our Team Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#17181B] via-[#2a2b30] to-[#17181B] text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-4">
              <GraduationCap className="w-4 h-4 inline mr-1" />
              Career Opportunity
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Your Degree Shouldn't Cost You Everything.</h2>
          </div>

          <Card className="bg-white/10 backdrop-blur-md border-2 border-[#FFE102]/50 shadow-2xl">
            <CardContent className="p-8 md:p-10">
              <div className="space-y-6 text-white/90 text-lg leading-relaxed">
                <p>
                  If you're a recent graduate or current college student carrying federal student loan debt, working or volunteering with <span className="text-[#FFE102] font-bold">Calvin D. Butts Inc.</span> could put you on the path to <span className="text-[#FFE102] font-bold">Public Service Loan Forgiveness (PSLF)</span> — a federal program that forgives your remaining student loan balance after 10 years of qualifying payments while working for a 501(c)(3) nonprofit like ours.
                </p>
                
                <p className="text-xl font-semibold text-white">
                  That means your time with us doesn't just change lives in the community — it could change yours financially.
                </p>

                <div className="bg-white/10 rounded-xl p-6 my-8">
                  <p className="mb-4">
                    <span className="text-[#FFE102] font-bold">Calvin D. Butts Inc.</span> is a registered 501(c)(3) nonprofit, which means full-time employees may qualify for PSLF. We're actively building our team across:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">Program Development</div>
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">Community Outreach</div>
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">Events</div>
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">STEAM Education</div>
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">Marketing</div>
                    <div className="bg-[#1167B1]/50 rounded-lg px-4 py-2 text-center text-sm font-semibold">Workforce Development</div>
                  </div>
                </div>

                <p>
                  If you're passionate about empowering underserved youth and want your career to work double duty — for your community <span className="text-[#FFE102]">AND</span> your financial future — we want to hear from you.
                </p>

                <div className="text-center pt-6 border-t border-white/20">
                  <p className="text-2xl font-bold text-[#FFE102] mb-4">
                    Join our team. Serve your community. Build toward loan freedom.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a href="mailto:info@calvinbuttsms.org?subject=Career%20Inquiry%20-%20PSLF%20Opportunity">
                      <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-lg px-8 py-6 rounded-full">
                        <Mail className="w-5 h-5 mr-2" />
                        Email Us to Apply
                      </Button>
                    </a>
                    <span className="text-white/60">or visit</span>
                    <a href="https://calvinbuttsms.org" className="text-[#FFE102] font-bold hover:underline flex items-center gap-1">
                      calvinbuttsms.org <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-[#17181B] mb-6">Let's Connect</h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're looking to enroll your child, volunteer, partner with us, or simply learn more about our programs, we're here to help. Reach out using any of the methods below.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#1167B1] text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-bold text-[#17181B]">Email</div>
                    <a href="mailto:info@calvinbuttsms.org" className="text-gray-600 hover:text-[#1167B1]">info@calvinbuttsms.org</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#1167B1] text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-bold text-[#17181B]">Phone</div>
                    <a href="tel:478-227-5849" className="text-gray-600 hover:text-[#1167B1]">478-227-5849</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#1167B1] text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-bold text-[#17181B]">Location</div>
                    <div className="text-gray-600">Metro Atlanta, GA</div>
                    <div className="text-sm text-gray-500 mt-1">Serving communities across Metro Atlanta including MLK (Adamsville)</div>
                  </div>
                </div>
              </div>

              {/* Photo */}
              <div className="mt-8">
                <img 
                  src="/images/gallery/IMG_1458.jpg" 
                  alt="Community impact" 
                  className="rounded-2xl shadow-xl w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-2 border-[#1167B1] shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#17181B]">Send Us a Message</CardTitle>
                <CardDescription>Fill out the form below and we'll get back to you within 24 hours.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      required 
                      className="mt-1"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleInputChange} 
                      required 
                      className="mt-1"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      type="tel" 
                      value={formData.phone} 
                      onChange={handleInputChange} 
                      className="mt-1"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message" 
                      name="message" 
                      value={formData.message} 
                      onChange={handleInputChange} 
                      required 
                      rows={6} 
                      className="mt-1"
                      placeholder="Tell us about your interest in our programs, questions, or how you'd like to get involved..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold py-6"
                  >
                    Submit Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Enrollment Info */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#1167B1] text-white mb-4">Enrollment Information</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">How to Enroll Your Child</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-[#1167B1] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Reach out via phone, email, or the form above to express your interest and ask questions.</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-[#1167B1] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <CardTitle>Learn More</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">We'll discuss program details, schedules, and answer any questions you have about enrollment.</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-[#1167B1] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <CardTitle>Get Started</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Complete enrollment paperwork and your child can begin their journey with us!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-[#1167B1]">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl text-[#17181B]">Office Hours & Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-xl text-[#17181B] mb-4">Office Hours</h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span className="font-semibold">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span className="font-semibold">By Appointment</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-semibold">Closed</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-xl text-[#17181B] mb-4">Response Time</h3>
                  <p className="text-gray-700 mb-4">We strive to respond to all inquiries within 24 hours during business days.</p>
                  <p className="text-gray-700">For urgent matters, please call us directly at <a href="tel:478-227-5849" className="font-semibold text-[#1167B1] hover:underline">478-227-5849</a>.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

export default Contact;